import type { ISamplePool } from '../../../../Model/SamplePool/ISamplePool.js'
import type { FileForProcessing } from '../../../../Model/Sample/ISample.js'
import type { IPipelineInformation } from '../../../../Model/Sample/IPipelineInformation.js'
import type { ProcessingProfile } from '../../../../Model/Order/processingProfile.js'
import type { OtuUnassignedSample } from '../../../../Generated/OtuUnassignedSample.js'
import { unassignedPercent } from '../../../../Model/Order/unassignedReads.js'

export interface Distribution {
  median: number
  mean: number
  min: number
  max: number
  n: number
}

export interface PerSampleRow {
  id: string
  name: string
  itemType: FileForProcessing['itemType']
  pool: string
  raw: number | null
  merged: number | null
  forwardTrimmed: number | null
  reverseTrimmed: number | null
  qualityFiltered: number | null
  /** unique sequences after per-sample dereplication; consensus sequences on ONT */
  unique: number | null
  /** percentage of the sample's OTU reads without a valid BLAST hit; null when the order's report records none */
  unassignedPercent: number | null
}

export interface SummaryStats {
  poolCount: number
  sampleCount: number
  negativeControlCount: number
  /** sum of per-item raw counts: read pairs on paired-end input, reads otherwise */
  rawReadsTotal: number
  /** fraction of pairs that merged, over samples with reads; null when merging was not applied */
  mergedFraction: Distribution | null
  /** median fraction of reads in which the primer was found, over samples with reads; null when primer removal was not applied */
  primerFoundForward: number | null
  primerFoundReverse: number | null
  qualityFilteredKept: number
  qualityFilteredDiscarded: number
  uniqueSequences: number
  /** reads in OTUs without a valid BLAST hit; null when the order's report records none */
  unassignedReadsTotal: number | null
  perSample: PerSampleRow[]
}

export const median = (values: readonly number[]): number | null => {
  if (values.length === 0) {
    return null
  }
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle]
}

export const distribution = (values: readonly number[]): Distribution | null => {
  const med = median(values)
  if (med == null) {
    return null
  }
  return {
    median: med,
    mean: values.reduce((sum, v) => sum + v, 0) / values.length,
    min: Math.min(...values),
    max: Math.max(...values),
    n: values.length
  }
}

const sum = (items: readonly FileForProcessing[], pick: (item: FileForProcessing) => number | null | undefined) =>
  items.reduce((acc, item) => acc + (pick(item) ?? 0), 0)

/* The step ratios describe samples; controls are expected to be near-empty, and the backend records a
 * total loss (ratio 0) for an item whose step saw no reads, which would drag the median. */
const ratioInputs = (
  items: readonly FileForProcessing[],
  reads: (p: IPipelineInformation) => number | null,
  ratio: (p: IPipelineInformation) => number | null
): number[] =>
  items.flatMap((item) => {
    const p = item.pipelineInformation
    if (item.itemType !== 'SAMPLE' || p == null || !reads(p)) {
      return []
    }
    const value = ratio(p)
    return value == null ? [] : [value]
  })

export const computeSummaryStats = (
  pools: readonly ISamplePool[],
  profile: Pick<ProcessingProfile, 'mergeApplied' | 'primerRemovalApplied'>,
  unassignedSamples: readonly OtuUnassignedSample[] = []
): SummaryStats => {
  const items = pools.flatMap((pool) => [...pool.samples, ...pool.negativeControls].map((item) => ({ item, pool: pool.label ?? pool.id })))
  const all = items.map(({ item }) => item)

  const mergedFraction = profile.mergeApplied
    ? distribution(
        ratioInputs(
          all,
          (p) => p.sequenceCountTotal,
          (p) => (p.sequenceCountMerged ?? 0) / (p.sequenceCountTotal ?? 1)
        )
      )
    : null

  // cutadapt's forward pass runs over every read plus its reverse complement, so a read whose primer
  // is found once counts as half of that pass's input: the loss is relative to 2N reads.
  const primerFoundForward = profile.primerRemovalApplied
    ? median(
        ratioInputs(
          all,
          (p) => p.sequenceCountMerged,
          (p) => (p.informationLossTrimmingForward == null ? null : (1 - p.informationLossTrimmingForward) * 2)
        )
      )
    : null
  const primerFoundReverse = profile.primerRemovalApplied
    ? median(
        ratioInputs(
          all,
          (p) => p.sequenceCountForwardTrimmed,
          (p) => (p.informationLossTrimmingReverse == null ? null : 1 - p.informationLossTrimmingReverse)
        )
      )
    : null

  // Reports from before unassigned OTUs were stored record none at all, which must not read as 0 %.
  const hasUnassigned = unassignedSamples.length > 0
  const unassignedById = new Map(unassignedSamples.map((u) => [u.sampleId, u]))
  const unassignedPercentOf = (id: string): number | null => {
    if (!hasUnassigned) return null
    const u = unassignedById.get(id)
    return u ? unassignedPercent(u.rawReads, u.assignedRawReads) : 0
  }

  const qualityFilteredKept = sum(all, (i) => i.pipelineInformation?.sequenceCountAfterQualityFiltered)
  const qualityFilteredBefore = sum(all, (i) => i.pipelineInformation?.sequenceCountBeforeQualityFiltered)

  return {
    poolCount: pools.length,
    sampleCount: pools.reduce((acc, pool) => acc + pool.samples.length, 0),
    negativeControlCount: pools.reduce((acc, pool) => acc + pool.negativeControls.length, 0),
    rawReadsTotal: sum(all, (i) => i.pipelineInformation?.sequenceCountTotal),
    mergedFraction,
    primerFoundForward,
    primerFoundReverse,
    qualityFilteredKept,
    qualityFilteredDiscarded: qualityFilteredBefore - qualityFilteredKept,
    uniqueSequences: sum(all, (i) => i.pipelineInformation?.dereplicateUniqueSequenceLength),
    unassignedReadsTotal: hasUnassigned ? unassignedSamples.reduce((acc, u) => acc + u.rawReads, 0) : null,
    perSample: items.map(({ item, pool }) => ({
      id: item.id,
      name: item.customerSampleName ?? item.forwardFile?.fileName ?? item.id,
      itemType: item.itemType,
      pool,
      raw: item.pipelineInformation?.sequenceCountTotal ?? null,
      merged: profile.mergeApplied ? (item.pipelineInformation?.sequenceCountMerged ?? null) : null,
      forwardTrimmed: profile.primerRemovalApplied ? (item.pipelineInformation?.sequenceCountForwardTrimmed ?? null) : null,
      reverseTrimmed: profile.primerRemovalApplied ? (item.pipelineInformation?.sequenceCountReverseTrimmed ?? null) : null,
      qualityFiltered: item.pipelineInformation?.sequenceCountAfterQualityFiltered ?? null,
      unique: item.pipelineInformation?.dereplicateUniqueSequenceLength ?? null,
      unassignedPercent: unassignedPercentOf(item.id)
    }))
  }
}
